import React, { useRef, useState } from "react";
import { useProject } from "../state/projectStore";
import { PAGE_FORMATS, getPageFormat } from "../lib/pageFormats";
import { PIECE_FORMATS, getPieceFormat, BLEED_OPTIONS } from "../lib/pieceFormats";
import { Upload, Trash2, RotateCw, ChevronUp, ChevronDown, FlipHorizontal, Grid3x3 } from "lucide-react";
import { toast } from "sonner";

const Section = ({ title, children, testid }) => (
  <div className="border-b border-[#31373E]" data-testid={testid}>
    <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#6B7280] bg-[#181B20]">
      {title}
    </div>
    <div className="p-4 flex flex-col gap-3">{children}</div>
  </div>
);

const Field = ({ label, children, hint }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
      {label}
    </label>
    {children}
    {hint && <span className="text-[10px] text-[#6B7280] font-mono">{hint}</span>}
  </div>
);

const NumInput = ({ value, onChange, step = 1, min = 0, max, suffix, testid }) => {
  const handleChange = (e) => {
    const v = parseFloat(e.target.value);
    if (Number.isNaN(v)) return;
    onChange(v);
  };
  const bump = (delta) => {
    const v = (value ?? 0) + delta;
    if (min !== undefined && v < min) return;
    if (max !== undefined && v > max) return;
    onChange(Math.round(v * 100) / 100);
  };
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={handleChange}
        data-testid={testid}
        className="h-8 flex-1 bg-[#121417] border border-[#31373E] text-sm px-2 rounded-sm font-mono text-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#00E5FF] focus:border-[#00E5FF]"
      />
      {suffix && (
        <span className="text-[10px] font-mono text-[#6B7280] w-4">{suffix}</span>
      )}
      <div className="flex flex-col">
        <button
          onClick={() => bump(step)}
          className="h-4 w-5 bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#31373E] flex items-center justify-center rounded-t-sm"
        >
          <ChevronUp size={10} />
        </button>
        <button
          onClick={() => bump(-step)}
          className="h-4 w-5 bg-[#252A30] border border-[#31373E] border-t-0 text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#31373E] flex items-center justify-center rounded-b-sm"
        >
          <ChevronDown size={10} />
        </button>
      </div>
    </div>
  );
};

// Page-offset controls. Uses LOCAL intent state for X/Y so typing a value does
// not write to the current page until the user clicks an "Apply" button. This
// is the correct UX for "set offset, then choose scope (current/odd/even/all)".
function OffsetControls({ totalPages, currentPage, pageOffsets, setPageOffset, applyOffsetToScope, resetPageOffsets }) {
  const [xIntent, setXIntent] = useState(0);
  const [yIntent, setYIntent] = useState(0);
  const currentOffset = pageOffsets[currentPage] || { x: 0, y: 0 };
  return (
    <>
      <div className="text-[10px] font-mono text-[#9CA3AF] flex justify-between">
        <span>Pág actual ({currentPage + 1})</span>
        <span>X={currentOffset.x} · Y={currentOffset.y} mm</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="X (intent)">
          <NumInput
            value={xIntent}
            onChange={setXIntent}
            step={0.1}
            suffix="mm"
            testid="page-offset-x-input"
          />
        </Field>
        <Field label="Y (intent)">
          <NumInput
            value={yIntent}
            onChange={setYIntent}
            step={0.1}
            suffix="mm"
            testid="page-offset-y-input"
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-1">
        <button
          onClick={() => {
            setPageOffset(currentPage, xIntent, yIntent);
            toast.success(`Página ${currentPage + 1}: ${xIntent}, ${yIntent} mm`);
          }}
          data-testid="offset-apply-current-btn"
          className="h-7 text-[10px] rounded-sm bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium"
        >
          A página actual
        </button>
        <button
          onClick={() => {
            applyOffsetToScope("all", xIntent, yIntent, totalPages);
            toast.success(`Todas las páginas: ${xIntent}, ${yIntent} mm`);
          }}
          data-testid="offset-apply-all-btn"
          className="h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]"
        >
          A todas
        </button>
        <button
          onClick={() => {
            applyOffsetToScope("odd", xIntent, yIntent, totalPages);
            toast.success(`Impares: ${xIntent}, ${yIntent} mm`);
          }}
          data-testid="offset-apply-odd-btn"
          className="h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]"
        >
          A impares
        </button>
        <button
          onClick={() => {
            applyOffsetToScope("even", xIntent, yIntent, totalPages);
            toast.success(`Pares: ${xIntent}, ${yIntent} mm`);
          }}
          data-testid="offset-apply-even-btn"
          className="h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]"
        >
          A pares
        </button>
      </div>
      <button
        onClick={() => {
          resetPageOffsets();
          setXIntent(0);
          setYIntent(0);
          toast.success("Desplazamientos reseteados");
        }}
        data-testid="offset-reset-btn"
        className="h-7 text-[10px] rounded-sm bg-transparent border border-[#31373E] text-[#6B7280] hover:text-[#F3F4F6]"
      >
        Reset todos
      </button>
    </>
  );
}

export default function LeftSidebar() {
  const {
    state,
    layout,
    page,
    setPage,
    setPiece,
    setCopies,
    setSeparation,
    setBleed,
    setCutMarks,
    addImages,
    removeImage,
    setDistribution,
    swapPieceDims,
    fillSheet,
    setPageOffset,
    applyOffsetToScope,
    resetPageOffsets,
  } = useProject();

  const fileRef = useRef(null);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const loaded = [];
    for (const f of list) {
      if (!f.type.startsWith("image/")) continue;
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(f);
      });
      const dims = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ w: 0, h: 0 });
        img.src = dataUrl;
      });
      loaded.push({
        id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: f.name,
        dataUrl,
        width_px: dims.w,
        height_px: dims.h,
      });
    }
    if (loaded.length) {
      addImages(loaded);
      toast.success(`${loaded.length} imagen(es) cargadas`);
    }
  };

  const handlePageFormat = (id) => {
    if (id === "custom") {
      setPage({ id: "custom", label: "Personalizado" });
      return;
    }
    const f = getPageFormat(id);
    setPage({
      id: f.id,
      label: f.label,
      width_mm: f.width_mm,
      height_mm: f.height_mm,
      printable_w_mm: f.printable_w_mm,
      printable_h_mm: f.printable_h_mm,
    });
  };

  const handlePieceFormat = (id) => {
    if (id === "custom") {
      setPiece({ id: "custom", label: "Personalizado" });
      return;
    }
    const f = getPieceFormat(id);
    setPiece({ id: f.id, label: f.label, width_mm: f.width_mm, height_mm: f.height_mm });
  };

  const toggleOrientation = () => {
    setPage({ orientation: page.orientation === "portrait" ? "landscape" : "portrait" });
  };

  return (
    <aside
      className="w-[320px] border-r border-[#31373E] bg-[#1C1F24] flex flex-col shrink-0 overflow-y-auto"
      data-testid="left-sidebar"
    >
      {/* IMÁGENES */}
      <Section title="Imágenes" testid="section-images">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="image-upload-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          data-testid="upload-images-btn"
          className="h-9 w-full rounded-sm border border-dashed border-[#31373E] bg-[#121417] hover:border-[#00E5FF] hover:bg-[#1C1F24] text-xs font-medium text-[#9CA3AF] hover:text-[#F3F4F6] transition-all flex items-center justify-center gap-2"
        >
          <Upload size={14} strokeWidth={1.75} />
          Subir imágenes
        </button>

        {state.images.length > 0 && (
          <>
            <div className="grid grid-cols-4 gap-1.5 mt-1">
              {state.images.map((img) => (
                <div
                  key={img.id}
                  className="relative group aspect-square border border-[#31373E] bg-[#121417] rounded-sm overflow-hidden"
                  data-testid={`image-thumb-${img.id}`}
                >
                  <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeImage(img.id)}
                    className="absolute top-0.5 right-0.5 h-5 w-5 rounded-sm bg-black/70 text-[#FF3B30] opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                    title="Eliminar"
                    data-testid={`remove-image-${img.id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </div>
            <Field label="Distribución">
              <div className="grid grid-cols-3 gap-1">
                <button
                  onClick={() => setDistribution("duplicate")}
                  data-testid="distribution-duplicate-btn"
                  title="Una imagen repetida en todos los marcos"
                  className={`h-8 text-[10px] rounded-sm border transition-colors ${
                    state.distribution === "duplicate"
                      ? "bg-[#00E5FF] text-black border-[#00E5FF] font-medium"
                      : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
                  }`}
                >
                  Duplicar
                </button>
                <button
                  onClick={() => setDistribution("sequence")}
                  data-testid="distribution-sequence-btn"
                  title="Una imagen distinta en cada marco (cíclico)"
                  className={`h-8 text-[10px] rounded-sm border transition-colors ${
                    state.distribution === "sequence"
                      ? "bg-[#00E5FF] text-black border-[#00E5FF] font-medium"
                      : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
                  }`}
                >
                  Secuencia
                </button>
                <button
                  onClick={() => setDistribution("page_per_image")}
                  data-testid="distribution-page-per-image-btn"
                  title="Cada imagen llena una página entera"
                  className={`h-8 text-[10px] rounded-sm border transition-colors ${
                    state.distribution === "page_per_image"
                      ? "bg-[#00E5FF] text-black border-[#00E5FF] font-medium"
                      : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
                  }`}
                >
                  1 pág/img
                </button>
              </div>
            </Field>
          </>
        )}
      </Section>

      {/* HOJA */}
      <Section title="Hoja" testid="section-page">
        <Field label="Formato">
          <div className="grid grid-cols-2 gap-1">
            {PAGE_FORMATS.map((f) => (
              <button
                key={f.id}
                onClick={() => handlePageFormat(f.id)}
                data-testid={`page-format-${f.id}`}
                className={`h-8 text-xs rounded-sm border transition-colors ${
                  state.page.id === f.id
                    ? "bg-[#00E5FF] text-black border-[#00E5FF] font-medium"
                    : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => handlePageFormat("custom")}
              data-testid="page-format-custom"
              className={`h-8 text-xs rounded-sm border transition-colors col-span-2 ${
                state.page.id === "custom"
                  ? "bg-[#00E5FF] text-black border-[#00E5FF] font-medium"
                  : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
              }`}
            >
              Personalizado
            </button>
          </div>
        </Field>

        <button
          onClick={toggleOrientation}
          data-testid="orientation-toggle"
          className="h-7 text-[10px] uppercase tracking-wider bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#31373E] rounded-sm flex items-center justify-center gap-2 transition-colors"
        >
          <RotateCw size={11} />
          {state.page.orientation === "portrait" ? "Vertical" : "Horizontal"}
        </button>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Ancho mm">
            <NumInput
              value={state.page.width_mm}
              onChange={(v) => setPage({ width_mm: v })}
              suffix="mm"
              testid="page-width-input"
            />
          </Field>
          <Field label="Alto mm">
            <NumInput
              value={state.page.height_mm}
              onChange={(v) => setPage({ height_mm: v })}
              suffix="mm"
              testid="page-height-input"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Útil W">
            <NumInput
              value={state.page.printable_w_mm}
              onChange={(v) => setPage({ printable_w_mm: v })}
              suffix="mm"
              testid="page-printable-w-input"
            />
          </Field>
          <Field label="Útil H">
            <NumInput
              value={state.page.printable_h_mm}
              onChange={(v) => setPage({ printable_h_mm: v })}
              suffix="mm"
              testid="page-printable-h-input"
            />
          </Field>
        </div>
      </Section>

      {/* PIEZA */}
      <Section title="Pieza" testid="section-piece">
        <Field label="Tamaño rápido">
          <div className="grid grid-cols-2 gap-1">
            {PIECE_FORMATS.map((f) => (
              <button
                key={f.id}
                onClick={() => handlePieceFormat(f.id)}
                data-testid={`piece-format-${f.id}`}
                className={`h-8 text-[10px] rounded-sm border transition-colors px-1 ${
                  state.piece.id === f.id
                    ? "bg-[#00E5FF] text-black border-[#00E5FF] font-medium"
                    : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => handlePieceFormat("custom")}
              data-testid="piece-format-custom"
              className={`h-8 text-[10px] rounded-sm border transition-colors col-span-2 ${
                state.piece.id === "custom"
                  ? "bg-[#00E5FF] text-black border-[#00E5FF] font-medium"
                  : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
              }`}
            >
              Personalizado
            </button>
          </div>
        </Field>
        <button
          onClick={swapPieceDims}
          data-testid="piece-swap-dims-btn"
          className="h-7 text-[10px] uppercase tracking-wider bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#31373E] rounded-sm flex items-center justify-center gap-2 transition-colors"
          title="Intercambiar ancho ↔ alto"
        >
          <FlipHorizontal size={11} />
          Girar pieza (W ↔ H)
        </button>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Ancho">
            <NumInput
              value={state.piece.width_mm}
              onChange={(v) => setPiece({ width_mm: v })}
              step={0.5}
              suffix="mm"
              testid="piece-width-input"
            />
          </Field>
          <Field label="Alto">
            <NumInput
              value={state.piece.height_mm}
              onChange={(v) => setPiece({ height_mm: v })}
              step={0.5}
              suffix="mm"
              testid="piece-height-input"
            />
          </Field>
        </div>
      </Section>

      {/* IMPOSICIÓN */}
      <Section title="Imposición" testid="section-imposition">
        <Field
          label="Copias"
          hint={`${layout.cols} × ${layout.rows} = ${layout.perPage}/hoja · ${layout.pages} hojas`}
        >
          <NumInput
            value={state.copies}
            onChange={setCopies}
            min={1}
            step={1}
            testid="copies-input"
          />
        </Field>
        <button
          onClick={() => {
            fillSheet();
            toast.success(`Hoja llena: ${layout.perPage} copias`);
          }}
          data-testid="fill-sheet-btn"
          className="h-8 text-[11px] uppercase tracking-wider bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium rounded-sm flex items-center justify-center gap-2"
          title="Calcular copias para llenar una hoja"
        >
          <Grid3x3 size={12} />
          Rellenar hoja
        </button>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Sep. H">
            <NumInput
              value={state.separation.h_mm}
              onChange={(v) => setSeparation({ h_mm: v })}
              step={0.5}
              suffix="mm"
              testid="separation-h-input"
            />
          </Field>
          <Field label="Sep. V">
            <NumInput
              value={state.separation.v_mm}
              onChange={(v) => setSeparation({ v_mm: v })}
              step={0.5}
              suffix="mm"
              testid="separation-v-input"
            />
          </Field>
        </div>
      </Section>

      {/* DESPLAZAMIENTO HOJA (compensación de impresora) */}
      <Section title="Desplazamiento de hoja" testid="section-page-offset">
        <span className="text-[10px] text-[#6B7280] leading-tight">
          Compensa desviaciones reales de la impresora. Mueve el bloque entero, no los marcos de corte individuales.
        </span>
        <OffsetControls
          totalPages={layout.pages || 1}
          currentPage={state.currentPage}
          pageOffsets={state.pageOffsets || {}}
          setPageOffset={setPageOffset}
          applyOffsetToScope={applyOffsetToScope}
          resetPageOffsets={resetPageOffsets}
        />
      </Section>

      {/* SANGRADO */}
      <Section title="Sangrado" testid="section-bleed">
        <div className="grid grid-cols-5 gap-1">
          {BLEED_OPTIONS.map((b) => (
            <button
              key={b}
              onClick={() => setBleed(b)}
              data-testid={`bleed-${b}-btn`}
              className={`h-8 text-xs rounded-sm border transition-colors ${
                state.bleed === b
                  ? "bg-[#FFB300] text-black border-[#FFB300] font-medium"
                  : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
              }`}
            >
              {b}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-[#6B7280] font-mono">mm a cada lado</span>
      </Section>

      {/* MARCAS DE CORTE */}
      <Section title="Marcas de corte" testid="section-marks">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={state.cutMarks.enabled}
            onChange={(e) => setCutMarks({ enabled: e.target.checked })}
            data-testid="cutmarks-enabled-checkbox"
            className="accent-[#00E5FF]"
          />
          <span className="text-xs text-[#F3F4F6]">Activar marcas</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Longitud">
            <NumInput
              value={state.cutMarks.length_mm}
              onChange={(v) => setCutMarks({ length_mm: v })}
              step={0.5}
              suffix="mm"
              testid="cutmarks-length-input"
            />
          </Field>
          <Field label="Distancia">
            <NumInput
              value={state.cutMarks.distance_mm}
              onChange={(v) => setCutMarks({ distance_mm: v })}
              step={0.5}
              suffix="mm"
              testid="cutmarks-distance-input"
            />
          </Field>
        </div>
        <Field label="Grosor (pt)">
          <NumInput
            value={state.cutMarks.weight_pt}
            onChange={(v) => setCutMarks({ weight_pt: v })}
            step={0.05}
            min={0.05}
            testid="cutmarks-weight-input"
          />
        </Field>
        <Field label="Páginas">
          <select
            value={state.cutMarks.pages}
            onChange={(e) => setCutMarks({ pages: e.target.value })}
            data-testid="cutmarks-pages-select"
            className="h-8 bg-[#121417] border border-[#31373E] text-xs px-2 rounded-sm text-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
          >
            <option value="all">Todas</option>
            <option value="first">Solo primera</option>
            <option value="odd">Impares</option>
            <option value="even">Pares</option>
          </select>
        </Field>
        <Field label="Tipo">
          <select
            value={state.cutMarks.kind || "piece"}
            onChange={(e) => setCutMarks({ kind: e.target.value })}
            data-testid="cutmarks-kind-select"
            className="h-8 bg-[#121417] border border-[#31373E] text-xs px-2 rounded-sm text-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
          >
            <option value="piece">Por pieza</option>
            <option value="block_outer">Solo esquinas exteriores</option>
          </select>
        </Field>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={state.cutMarks.showBorderFrame}
            onChange={(e) => setCutMarks({ showBorderFrame: e.target.checked })}
            data-testid="cutmarks-border-checkbox"
            className="accent-[#00E5FF]"
          />
          <span className="text-xs text-[#F3F4F6]">Marco fino negro</span>
        </label>
      </Section>
    </aside>
  );
}
